import { z } from "zod";
import { webDomainSchema } from "./tools.ts";

/**
 * Web fetch (openspec change `agent-web-fetch`): the agent reads one public page. Core declares
 * the tool; the fetcher (`apps/fetcher`, a function outside the data network) runs the request.
 * These schemas are the contract between the provider and the fetcher: no identifier of a
 * tenant, user, project or conversation crosses it in either direction.
 */

/** The model-facing name of the web fetch tool (its UI parts are `tool-web_fetch`). */
export const WEB_FETCH_TOOL_NAME = "web_fetch";

/** Longest URL the agent may ask for. */
export const WEB_FETCH_MAX_URL_CHARS = 2048;
/** Longest URL the fetcher returns: a longer redirect or link is refused or dropped. */
const MAX_RETURNED_URL_CHARS = WEB_FETCH_MAX_URL_CHARS;
/** Longest page title returned. */
export const WEB_FETCH_MAX_TITLE_CHARS = 300;
/** Longest link text returned. */
export const WEB_FETCH_MAX_LINK_TEXT_CHARS = 200;

/** What one fetch may cost at most: bytes read, characters returned, hops, time and links. */
export const webFetchLimitsSchema = z.strictObject({
  maxBytes: z
    .int()
    .min(1)
    .max(5 * 1024 * 1024),
  maxChars: z.int().min(1).max(100_000),
  maxRedirects: z.int().min(0).max(10),
  timeoutMs: z.int().min(100).max(25_000),
  maxLinks: z.int().min(0).max(500),
});
export type WebFetchLimits = z.infer<typeof webFetchLimitsSchema>;

/** The platform's limits (design D4): 2 MiB, 40,000 characters, 5 hops, 20 s, 200 links. */
export const WEB_FETCH_LIMITS: WebFetchLimits = {
  maxBytes: 2 * 1024 * 1024,
  maxChars: 40_000,
  maxRedirects: 5,
  timeoutMs: 20_000,
  maxLinks: 200,
};

/** Normalised domains: a root domain covers its subdomains. */
const domainListSchema = z.array(webDomainSchema).max(1000);

/** What the provider sends the fetcher. */
export const webFetchRequestSchema = z.strictObject({
  url: z.string().max(WEB_FETCH_MAX_URL_CHARS),
  limits: webFetchLimitsSchema,
  /** The tenant's resolved lists, the instance deny list merged into `exclude`. */
  domains: z.strictObject({ include: domainListSchema, exclude: domainListSchema }),
  /**
   * Host names never fetched, with their subdomains, besides the fetcher's own list
   * (`ESI_WEB_FETCH_DENIED_HOSTS`, the stage's public hosts): never shown to the agent.
   */
  deniedHosts: domainListSchema.optional(),
});
export type WebFetchRequest = z.infer<typeof webFetchRequestSchema>;

/** A link of the page, resolved against its final URL. */
export const webFetchLinkSchema = z.strictObject({
  url: z.string().max(MAX_RETURNED_URL_CHARS),
  text: z.string().max(WEB_FETCH_MAX_LINK_TEXT_CHARS),
});

/** A fetched page: what any web fetch provider returns. */
export const webFetchOutputSchema = z.strictObject({
  finalUrl: z.string().max(MAX_RETURNED_URL_CHARS),
  status: z.int().min(100).max(599),
  contentType: z.string().max(200),
  title: z.string().max(WEB_FETCH_MAX_TITLE_CHARS).optional(),
  /** Markdown (HTML converted by the fetcher) or plain text. */
  text: z.string().max(WEB_FETCH_LIMITS.maxChars),
  /** The body or the text was cut at a limit. */
  truncated: z.boolean(),
  links: z.array(webFetchLinkSchema).max(WEB_FETCH_LIMITS.maxLinks),
  /** Body bytes read (decompressed). */
  bytes: z.int().min(0),
  /** Redirects followed. */
  redirects: z.int().min(0).max(10),
});
export type WebFetchOutput = z.infer<typeof webFetchOutputSchema>;

/**
 * Why the fetcher returned no page. Refused before anything was sent, or by a hop's checks:
 * `invalid_url`, `host_denied` (IP literal, local or internal name, port, the stage's own
 * hosts), `address_denied` (a resolved address in a private, loopback, link-local, metadata,
 * CGNAT, multicast or reserved range), `domain_denied` (the tenant's lists),
 * `redirect_denied` (a downgrade to `http`, or a hop failing a check), `unsupported_type`.
 * Failed: `redirect_limit`, `timeout`, `upstream`; `too_large` is reserved for a provider that
 * refuses a large page rather than reading its beginning (the platform's fetcher truncates).
 */
export const WEB_FETCH_ERROR_CODES = [
  "invalid_url",
  "host_denied",
  "address_denied",
  "domain_denied",
  "redirect_limit",
  "redirect_denied",
  "timeout",
  "too_large",
  "unsupported_type",
  "upstream",
] as const;
export type WebFetchErrorCode = (typeof WEB_FETCH_ERROR_CODES)[number];

/** Codes a policy or the content refused: the agent is told, nothing is billed as a failure. */
export const WEB_FETCH_REFUSALS: readonly WebFetchErrorCode[] = [
  "invalid_url",
  "host_denied",
  "address_denied",
  "domain_denied",
  "redirect_denied",
  "unsupported_type",
];

export const webFetchErrorSchema = z.strictObject({
  error: z.enum(WEB_FETCH_ERROR_CODES),
  /** The upstream status, when one answered. */
  status: z.int().min(100).max(599).optional(),
});
export type WebFetchError = z.infer<typeof webFetchErrorSchema>;

/** What the fetcher answers: a page or an error code, never both. */
export const webFetchResponseSchema = z.union([webFetchOutputSchema, webFetchErrorSchema]);
export type WebFetchResponse = z.infer<typeof webFetchResponseSchema>;

/**
 * `tools.webFetch.approval`: `provenance` asks only for URLs the conversation never saw;
 * `always` asks for every fetch.
 */
export const WEB_FETCH_APPROVAL_MODES = ["provenance", "always"] as const;
export const webFetchApprovalSchema = z.enum(WEB_FETCH_APPROVAL_MODES);
export type WebFetchApproval = z.infer<typeof webFetchApprovalSchema>;
